import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CssBaseline,
  Container,
  ThemeProvider,
  createTheme,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Snackbar
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Html5QrcodeScanner, Html5QrcodeScanType, Html5Qrcode } from "html5-qrcode";
import AppAppBar from "../../GeneralComponents/AppAppBar";
import Hero from "./components/Hero";
import Footer from "./components/Footer";
import Theme from "../../GeneralComponents/Theme";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

const AlertComponent = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ScannerPage() {
  const [mode, setMode] = useState("light");
  const theme = createTheme(Theme(mode));
  const navigate = useNavigate();
  const scannerRef = useRef(null);

  const [scanResult, setScanResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  useEffect(() => {
    initializeScanner();
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, []);

  const initializeScanner = () => {
    try {
      // Check if we're on a mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const scanner = new Html5QrcodeScanner(
        "qr-reader",
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          // Hide camera selection UI and auto-select back camera
          showTorchButtonIfSupported: false,
          showZoomSliderIfSupported: false,
          defaultZoomValueIfSupported: 2,
          // Auto-select back camera on mobile
          cameraIdOrConfig: isMobile ? {
            facingMode: { exact: "environment" } // Back camera
          } : undefined
        },
        false
      );

      scanner.render(onScanSuccess, onScanFailure);
      scannerRef.current = scanner;
    } catch (error) {
      console.error("Error initializing scanner:", error);
      setError("Failed to initialize camera. Please check camera permissions.");
    }
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    if (isProcessing) return; // Prevent multiple simultaneous processing
    
    setIsProcessing(true);
    setScanResult(decodedText);
    
    try {
      // Stop scanning temporarily
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
      
      // Process the scanned QR code
      await processRedemptionCode(decodedText);
    } catch (error) {
      console.error("Error processing QR code:", error);
      setError("Failed to process QR code. Please try again.");
      // Restart scanner after error
      setTimeout(() => {
        initializeScanner();
        setIsProcessing(false);
      }, 2000);
    }
  };

  const onScanFailure = (error) => {
    // Handle scan failure silently - this is normal for poor lighting, etc.
    console.log("Scan failed:", error);
  };

  const processRedemptionCode = async (redemptionCode) => {
    try {
      const response = await fetch('https://sentry-server-726023e2520c.herokuapp.com/api/parse-redemption-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redemption_code: redemptionCode
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setSnackbarMessage(`Success! Redirecting to claim page...`);
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        // Redirect to claim page with verification code
        setTimeout(() => {
          navigate(`/claim/${data.verificationCode}`);
        }, 1500);
      } else {
        throw new Error("Invalid redemption code");
      }
    } catch (error) {
      console.error("API Error:", error);
      setSnackbarMessage("Invalid QR code. Please try again.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      
      // Restart scanner after error
      setTimeout(() => {
        initializeScanner();
        setIsProcessing(false);
      }, 2000);
    }
  };

  const handleRetry = () => {
    setError(null);
    setScanResult(null);
    setIsProcessing(false);
    initializeScanner();
  };

  const handleBack = () => {
    if (scannerRef.current) {
      scannerRef.current.clear();
    }
    navigate(-1);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppAppBar
        mode={mode}
        toggleColorMode={() =>
          setMode((prevMode) => (prevMode === "light" ? "dark" : "light"))
        }
      />
      <Hero />
      <Container maxWidth="md" sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            QR Code Scanner
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Position the QR code within the scanning area. Make sure the code is well-lit and clearly visible.
          </Typography>
          
          {isProcessing && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography>Processing QR code...</Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            id="qr-reader"
            sx={{
              width: '100%',
              maxWidth: 400,
              mx: 'auto',
              '& video': {
                borderRadius: 2,
              },
              // Hide camera selection UI
              '& .camera-selection': {
                display: 'none !important',
              },
              '& .camera-selection-ui': {
                display: 'none !important',
              },
              '& .camera-selection-container': {
                display: 'none !important',
              },
              '& .camera-selection-panel': {
                display: 'none !important',
              },
              '& .camera-selection-button': {
                display: 'none !important',
              },
              '& .camera-selection-dropdown': {
                display: 'none !important',
              }
            }}
          />
          
          {error && error.includes('camera') && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.contrastText">
                Camera access is required to scan QR codes. Please allow camera permissions and refresh the page.
              </Typography>
            </Box>
          )}



          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleRetry}
              startIcon={<QrCodeScannerIcon />}
            >
              Restart Scanner
            </Button>
          </Box>
        </Paper>
      </Container>
      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <AlertComponent
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </AlertComponent>
      </Snackbar>
    </ThemeProvider>
  );
} 